import {useContext, useState} from 'react';

import {FileDownload} from '@mui/icons-material';
import {Button, CircularProgress, Tooltip} from '@mui/material';

import {fromDateToSmall} from '../../../utils/date-utils';
import {GroupContestViewDataQueryResponse} from '../../../api/responses/ContestViewResponseTypes';
import {request, URLs} from '../../../api';
import {exportDownloadCSV} from '../../../utils/CsvHelper';
import {
  ContestSubmissionForCsvExportQueryParams,
  ContestSubmissionForCsvExportResponse,
} from '../../../api/responses/GroupSubmissionsExportTypes';
import AppContext from '../../../context/AppContext';

type SubmissionsExportButtonProps = {
  contest: GroupContestViewDataQueryResponse;
  groupContest: number;
  searchParams: {
    problemId?: number;
    userId?: number;
    total?: number;
    latest?: boolean;
    duringContest?: boolean;
  };
};

function SubmissionsExportButton(props: SubmissionsExportButtonProps) {
  const {dispatchError} = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(false);

  const prepareCsvAndExport = (data: ContestSubmissionForCsvExportResponse) => {
    const headers = [
      'email',
      'full name',
      'problem',
      'status',
      'points',
      'correctness',
      'submitted-at',
      'during-contest',
      'code',
    ];

    const preparedData = data.map((row) => ({
      email: row.email,
      'full name': `${row.firstName} ${row.lastName}`,
      problem: row.problemName || 'Unknown problem name',
      status: row.compiled ? (row.correctness == 1 ? 'Completed' : 'Wrong answer') : 'Compile error',
      points: String(row.grade),
      correctness: row.correctness ? `${(row.correctness * 100).toFixed(2)}%` : '-',
      'submitted-at': fromDateToSmall(new Date(row.submittedAt)),
      'during-contest': row.duringContest ? 'Yes' : 'No',
      code: `"${row.code.replace(/(\r\n|\r|\n)/g, '\\n').replace(/"/g, '""')}"`,
    }));

    exportDownloadCSV(headers, preparedData, `Submissions for ${props.contest.title} - ${fromDateToSmall(new Date())}`);
  };

  const exportSubmissions = async () => {
    const searchParams = new URLSearchParams();

    Object.entries({
      ...props.searchParams,
    }).forEach(([key, value]) => {
      if (value != null) {
        searchParams.append(key, value.toString());
      }
    });
    setIsLoading(true);
    await request<ContestSubmissionForCsvExportQueryParams, never, ContestSubmissionForCsvExportResponse>(
      URLs.getAllGroupContestSubmissionsForCsvExport(Number(props.groupContest)),
      {
        method: 'GET',
        params: {
          duringContest: props.searchParams.duringContest ? 'true' : 'false',
          latest: props.searchParams.latest ? 'true' : 'false',
          problemId: props.searchParams.problemId ? String(props.searchParams.problemId) : undefined,
          userId: props.searchParams.userId ? String(props.searchParams.userId) : undefined,
        },
        successCallback(response) {
          prepareCsvAndExport(response);
          setIsLoading(false);
        },
        errorCallback() {
          dispatchError({errorMessage: 'There was an error. Please try again later'});
          setIsLoading(false);
        },
      }
    );
  };

  return (
    <>
      <Tooltip title="Exports all submission entries based on the selected filters">
        <Button
          startIcon={isLoading ? <CircularProgress color="inherit" size="1.1rem" /> : <FileDownload />}
          size="small"
          color="primary"
          variant="contained"
          onClick={() => exportSubmissions()}
        >
          Export to CSV
        </Button>
      </Tooltip>
    </>
  );
}

export default SubmissionsExportButton;
